import {EditorView} from '@codemirror/view'
import {h} from 'hastscript'

import {buildToggledTasklistPrefixSyntax, parseTasklistPrefix} from '../../language-tools/markdown/list-utils'
import {HastElementWidget} from './hast-element'

type TaskListCheckboxWidgetProps = {
  checked: boolean
  from: number
  to: number
}

function createCheckboxNode(checked: boolean) {
  return h('input', {
    type: 'checkbox',
    tabindex: -1,
    checked: checked ? true : undefined,
    'aria-label': checked ? 'Checked task list item' : 'Unchecked task list item',
    'data-task-list-checkbox': '',
  })
}

export class TaskListCheckboxWidget extends HastElementWidget {
  #checked: boolean
  #renderedChecked: boolean
  #from: number
  #to: number
  #view?: EditorView
  #input?: HTMLInputElement
  #pendingToggleToken?: symbol

  constructor(props: TaskListCheckboxWidgetProps) {
    super(createCheckboxNode(props.checked))
    this.#checked = props.checked
    this.#renderedChecked = props.checked
    this.#from = props.from
    this.#to = props.to
  }

  #onMouseDown = (event: MouseEvent) => {
    event.preventDefault()
  }

  #onChange = (event: Event) => {
    event.preventDefault()
  }

  #onClick = (event: MouseEvent) => {
    event.preventDefault()

    const view = this.#view
    if (!view) return

    const currentPrefix = view.state.doc.sliceString(this.#from, this.#to)
    const currentlyChecked = parseTasklistPrefix(currentPrefix)?.value.toLowerCase() === 'x'
    const nextChecked = !currentlyChecked

    this.#dispatchToggle(view, nextChecked)
  }

  #removeEventListeners(input?: HTMLInputElement) {
    if (!input) return

    input.removeEventListener('mousedown', this.#onMouseDown)
    input.removeEventListener('change', this.#onChange)
    input.removeEventListener('click', this.#onClick)
  }

  #dispatchToggle(view: EditorView, nextChecked: boolean) {
    const currentPrefix = view.state.doc.sliceString(this.#from, this.#to)
    const charAfterPrefix = view.state.doc.sliceString(this.#to, this.#to + 1)
    const nextPrefix = buildToggledTasklistPrefixSyntax(nextChecked, currentPrefix, charAfterPrefix)

    view.dispatch({
      changes: {from: this.#from, to: this.#to, insert: nextPrefix},
      selection: view.state.selection,
    })

    // Update the target offset metadata so subsequent toggles operate on the new prefix length.
    this.#to = this.#from + nextPrefix.length

    if (this.#input) {
      this.#input.setAttribute('data-task-list-to', String(this.#to))
    }

    if (this.#view) {
      const viewRef = this.#view
      const fromRef = this.#from
      const toRef = this.#to
      const toggleToken = Symbol('taskListToggle')
      this.#pendingToggleToken = toggleToken

      queueMicrotask(() => {
        if (this.#view !== viewRef) return
        if (this.#pendingToggleToken !== toggleToken) return
        if (this.#from !== fromRef || this.#to !== toRef) return

        this.#pendingToggleToken = undefined
      })
    }
  }

  override toDOM(view: EditorView) {
    this.#view = view

    const input = super.toDOM(view) as HTMLInputElement

    if (this.#input && this.#input !== input) {
      this.#removeEventListeners(this.#input)
    }

    this.#input = input
    input.tabIndex = -1
    this.#renderedChecked = this.#checked
    input.checked = this.#renderedChecked
    input.defaultChecked = this.#renderedChecked
    input.toggleAttribute('checked', this.#renderedChecked)
    input.setAttribute('aria-label', this.#renderedChecked ? 'Checked task list item' : 'Unchecked task list item')
    input.setAttribute('data-task-list-from', String(this.#from))
    input.setAttribute('data-task-list-to', String(this.#to))

    input.addEventListener('mousedown', this.#onMouseDown)
    input.addEventListener('change', this.#onChange)
    input.addEventListener('click', this.#onClick)

    return input
  }

  override destroy(dom: HTMLElement) {
    if (dom instanceof HTMLInputElement) {
      this.#removeEventListeners(dom)
      if (dom === this.#input) this.#input = undefined
    }
    this.#view = undefined
  }

  override eq(other: HastElementWidget) {
    if (!(other instanceof TaskListCheckboxWidget)) return false
    // Force CodeMirror to re-render the widget after every document update so the checkbox
    // always reflects the latest markdown representation.
    return false
  }

  override ignoreEvent() {
    return true
  }
}

export type {TaskListCheckboxWidgetProps}
